import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { Upload, User, Save } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import Layout from './Layout';
// import toast from 'react-hot-toast';

interface ProfileFormData {
  firstName: string;
  lastName: string;
  phoneNumber: string;
  city: string;
  title: string;
  equityPercentage: number;
  isTechnicalFounder: boolean;
  workHistory: string;
  education: string;
}

const ProfilePage: React.FC = () => {
  const navigate = useNavigate();
  const { user, updateProfile, uploadProfilePicture } = useAuth();
  const { themeClasses, isLightTheme } = useTheme();
  const [isLoading, setIsLoading] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset
  } = useForm<ProfileFormData>({
    defaultValues: {
      firstName: user?.firstName || '',
      lastName: user?.lastName || '',
      phoneNumber: user?.phoneNumber || '',
      city: user?.city || '',
      title: user?.title || '',
      equityPercentage: user?.equityPercentage || 0,
      isTechnicalFounder: user?.isTechnicalFounder || false,
      workHistory: user?.workHistory || '',
      education: user?.education || ''
    }
  });

  useEffect(() => {
    if (user) {
      reset({
        firstName: user.firstName || '',
        lastName: user.lastName || '',
        phoneNumber: user.phoneNumber || '',
        city: user.city || '',
        title: user.title || '',
        equityPercentage: user.equityPercentage || 0,
        isTechnicalFounder: user.isTechnicalFounder || false,
        workHistory: user.workHistory || '',
        education: user.education || '',
      });
    }
  }, [user, reset]);

  const onSubmit = async (data: ProfileFormData) => {
    if (!isEditing) return; // Prevent submission when not in editing mode
    
    setIsLoading(true);
    try {
      const result = await updateProfile(data);
      if (result.success) {
        setIsEditing(false);
      }
    } catch (error) {
      console.error('Error updating profile:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleEditClick = (e: React.MouseEvent) => {
    e.preventDefault();
    setIsEditing(true);
  };

  const handleProfilePictureUpload = () => {
    fileInputRef.current?.click();
  };

  const handleFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      alert('Please select an image file');
      return;
    }

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      alert('File size must be less than 5MB');
      return;
    }

    setIsUploading(true);
    try {
      const result = await uploadProfilePicture(file);
      
      if (result.success) {
        // Clear the file input
        if (fileInputRef.current) {
          fileInputRef.current.value = '';
        }
      }
    } catch (error) {
      console.error('Error uploading profile picture:', error);
    } finally {
      setIsUploading(false);
    }
  };

  if (!user) return null;

  const profileTitle = user.accountType === 'founder' ? 'Founder Profile' : 'Investor Profile';

  return (
    <Layout title={profileTitle} showBackButton onBack={() => navigate('/')}>

      {/* Profile Picture */}
      <div className="p-4">
        <div className={`${themeClasses.card} text-center rounded-lg`}>
          <div className="relative inline-block">
            <div className={`w-24 h-24 ${isLightTheme ? 'bg-gradient-to-r from-cyan-100 to-blue-100' : 'bg-gray-600'} rounded-full flex items-center justify-center mx-auto mb-4 shadow-lg`}>
              {user.profilePictureUrl ? (
                <img
                  src={user.profilePictureUrl}
                  alt={`${user.firstName} ${user.lastName}`}
                  className="w-24 h-24 rounded-full object-cover"
                />
              ) : (
                <User className={`w-12 h-12 ${themeClasses.textSecondary}`} />
              )}
            </div>
            <button
              type="button"
              onClick={handleProfilePictureUpload}
              disabled={isUploading}
              className={`absolute bottom-0 right-0 p-2 ${isLightTheme ? 'bg-gradient-to-r from-cyan-500 to-blue-600 hover:from-cyan-600 hover:to-blue-700' : 'bg-primary-500'} text-white rounded-full shadow-lg hover:shadow-xl transition-all duration-300 disabled:opacity-50`}
            >
              {isUploading ? (
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
              ) : (
                <Upload className="w-4 h-4" />
              )}
            </button>
          </div>
          <input
            type="file"
            accept="image/*"
            onChange={handleFileChange}
            className="hidden"
            ref={fileInputRef}
          />
          <h2 className={`text-xl font-semibold ${themeClasses.text} mb-1`}>
            {user.firstName} {user.lastName}
          </h2>
          <p className={`${themeClasses.textSecondary} capitalize`}>
            {user.accountType.replace('_', ' ')}
          </p>
        </div>
      </div>

      {/* Profile Form */}
      <div className="p-4">
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
          {/* Personal Information */}
          <div className={`${themeClasses.card} rounded-lg`}>
            <h3 className={`text-lg font-semibold ${themeClasses.text} mb-4`}>Personal Information</h3>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className={`block text-sm font-medium ${themeClasses.textSecondary} mb-2`}>
                  First Name
                </label>
                <input
                  {...register('firstName', { required: 'First name is required' })}
                  className={`${themeClasses.input} w-full px-3 py-2`}
                  placeholder="Enter first name"
                  disabled={!isEditing}
                />
                {errors.firstName && (
                  <p className="text-red-400 text-sm mt-1">{errors.firstName.message}</p>
                )}
              </div>

              <div>
                <label className={`block text-sm font-medium ${themeClasses.textSecondary} mb-2`}>
                  Last Name
                </label>
                <input
                  {...register('lastName', { required: 'Last name is required' })}
                  className={`${themeClasses.input} w-full rounded-lg px-3 py-2`}
                  placeholder="Enter last name"
                  disabled={!isEditing}
                />
                {errors.lastName && (
                  <p className="text-red-400 text-sm mt-1">{errors.lastName.message}</p>
                )}
              </div>

              <div>
                <label className={`block text-sm font-medium ${themeClasses.textSecondary} mb-2`}>
                  Phone Number
                </label>
                <input
                  {...register('phoneNumber')}
                  className={`${themeClasses.input} w-full rounded-lg px-3 py-2`}
                  placeholder="Enter phone number"
                  disabled={!isEditing}
                />
              </div>

              <div>
                <label className={`block text-sm font-medium ${themeClasses.textSecondary} mb-2`}>
                  City
                </label>
                <input
                  {...register('city')}
                  className={`${themeClasses.input} w-full rounded-lg px-3 py-2`}
                  placeholder="Enter city"
                  disabled={!isEditing}
                />
              </div>

              <div>
                <label className={`block text-sm font-medium ${themeClasses.textSecondary} mb-2`}>
                  Title
                </label>
                <input
                  {...register('title')}
                  className={`${themeClasses.input} w-full rounded-lg px-3 py-2`}
                  placeholder="Enter your title"
                  disabled={!isEditing}
                />
              </div>

              <div>
                <label className={`block text-sm font-medium ${themeClasses.textSecondary} mb-2`}>
                  Equity Percentage
                </label>
                <input
                  {...register('equityPercentage', { valueAsNumber: true })}
                  type="number"
                  min="0"
                  max="100"
                  className={`${themeClasses.input} w-full rounded-lg px-3 py-2`}
                  placeholder="Enter equity percentage"
                  disabled={!isEditing}
                />
              </div>
            </div>

            <div className="mt-4">
              <div className="flex items-center">
                <input
                  {...register('isTechnicalFounder')}
                  type="checkbox"
                  className={`w-4 h-4 text-primary-600 ${isLightTheme ? 'bg-white border-gray-300' : 'bg-gray-700 border-gray-600'} rounded focus:ring-primary-500 focus:ring-2`}
                  disabled={!isEditing}
                />
                <label className={`ml-2 text-sm ${themeClasses.textSecondary}`}>
                  Technical Founder
                </label>
              </div>
            </div>

            <div className="mt-4">
              <label className={`block text-sm font-medium ${themeClasses.textSecondary} mb-2`}>
                Work History
              </label>
              <textarea
                {...register('workHistory')}
                className={`${themeClasses.input} w-full h-20 resize-none rounded-lg px-3 py-2`}
                placeholder="Describe your work experience..."
                disabled={!isEditing}
              />
            </div>

            <div>
              <label className={`block text-sm font-medium ${themeClasses.textSecondary} mb-2`}>
                Education
              </label>
              <textarea
                {...register('education')}
                className={`${themeClasses.input} w-full h-20 resize-none rounded-lg px-3 py-2`}
                placeholder="Describe your educational background..."
                disabled={!isEditing}
              />
            </div>
          </div>

          {/* Account Information */}
          <div className={`${themeClasses.card} rounded-lg`}>
            <h3 className={`text-lg font-semibold ${themeClasses.text} mb-4`}>Account Information</h3>
            
            <div className="space-y-3">
              <div>
                <label className={`block text-sm font-medium ${themeClasses.textSecondary}`}>Email</label>
                <p className={themeClasses.text}>{user.email}</p>
              </div>
              <div>
                <label className={`block text-sm font-medium ${themeClasses.textSecondary}`}>Account Type</label>
                <p className={`${themeClasses.text} capitalize`}>{user.accountType.replace('_', ' ')}</p>
              </div>
              <div>
                <label className={`block text-sm font-medium ${themeClasses.textSecondary}`}>Email Verified</label>
                <p className={`${user.isEmailVerified ? 'text-green-400' : 'text-red-400'}`}>
                  {user.isEmailVerified ? 'Yes' : 'No'}
                </p>
              </div>
              <div>
                <label className={`block text-sm font-medium ${themeClasses.textSecondary}`}>Member Since</label>
                <p className={themeClasses.text}>
                  {new Date(user.createdAt || '').toLocaleDateString()}
                </p>
              </div>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex space-x-4">
            {isEditing ? (
              <>
                <button
                  type="submit"
                  disabled={isLoading}
                  className={`${themeClasses.button} flex-1 flex items-center justify-center px-4 py-2 rounded-lg font-medium transition-colors duration-200`}
                >
                  <Save className="w-4 h-4 mr-2" />
                  {isLoading ? 'Saving...' : 'Save Changes'}
                </button>
                <button
                  type="button"
                  onClick={() => setIsEditing(false)}
                  className={`${themeClasses.buttonSecondary} px-6 py-2 rounded-lg font-medium transition-colors duration-200`}
                >
                  Cancel
                </button>
              </>
            ) : (
              <button
                type="button"
                onClick={handleEditClick}
                className={`${themeClasses.button} flex-1 px-4 py-2 rounded-lg font-medium transition-colors duration-200`}
              >
                Edit Profile
              </button>
            )}
          </div>
        </form>
      </div>
    </Layout>
  );
};

export default ProfilePage; 